import { VERBS, NAMED_ROLES, stepNamesFor, ivrVolumeGain } from "./verbs";

// Compiles the react-flow graph into the numera-apps-server payload:
//   { name, description, trigger, handlers }
// plus a list of human-readable warnings (dangling hooks, missing branches…).
//
// Model:
//   - "next" edges continue the SAME handler (inline sequence)
//   - action/wait/to/transcription edges target NAMED handlers (handlers{} keys)
//   - branch case edges target INLINE sequences embedded in the branch
export function compile(elements, meta) {
  const nodes = elements.filter(e => !e.source && !e.target);
  // clona as arestas — a blindagem abaixo reescreve sourceHandle e não pode
  // mutar o estado do editor (elements é a fonte do react-flow).
  const edges = elements.filter(e => e.source && e.target).map(e => ({ ...e }));
  const stepMap = stepNamesFor(nodes); // nodeId -> step name (for _name)
  const warnings = [];

  const nodeById = {};
  nodes.forEach(n => {
    nodeById[n.id] = n;
  });

  // Blindagem (conserta fluxos já salvos, ex.: gerados pelo copiloto): um nó cujo
  // ÚNICO handle é de papel nomeado (gather/dial → "action", redirect → "to")
  // PAUSA a chamada e continua pelo hook nomeado — nunca inline. Se a continuação
  // foi ligada por "next" (default cego do gerador), reescreve essa aresta para o
  // handle nomeado; senão o verbo sai sem actionHook e a chamada morre no gather.
  edges.forEach(e => {
    const src = nodeById[e.source];
    const def = src && src.data && VERBS[src.data.verbKey];
    const handles = (def && def.handles) || [];
    if (handles.length !== 1) return;
    const only = handles[0].id;
    if (NAMED_ROLES.indexOf(only) < 0) return; // handle único precisa ser nomeado
    if ((e.sourceHandle || "next") !== "next") return; // já está num handle nomeado
    if (edges.some(x => x.source === e.source && x.sourceHandle === only)) return; // já há aresta certa
    e.sourceHandle = only;
  });

  // edgeMap[`${sourceId}::${handle}`] = targetId
  const edgeMap = {};
  edges.forEach(e => {
    edgeMap[`${e.source}::${e.sourceHandle || "next"}`] = e.target;
  });

  const isEntry = n =>
    n.data && (n.data.verbKey === "__start__" || n.data.verbKey === "__trigger__");
  const start = nodes.find(isEntry);
  if (!start) {
    return {
      definition: { ...meta, handlers: {} },
      warnings: ["Falta o gatilho (handler /)."],
    };
  }
  if (start.data.verbKey === "__trigger__" && !start.data.triggerType) {
    warnings.push("Escolha um gatilho para a automação.");
  }

  // 1) named handler heads = start + targets of NAMED-role edges
  const namedHeads = new Set([start.id]);
  edges.forEach(e => {
    const src = nodeById[e.source];
    if (src && src.data && src.data.disabled) return; // disabled node yields no handler
    if (NAMED_ROLES.indexOf(e.sourceHandle) >= 0) namedHeads.add(e.target);
    // Menu WhatsApp: cada saída de opção vira um handler nomeado (o motor retoma
    // nelas pelo cursor _wait). Opções são dinâmicas, então não dá pra pôr em
    // NAMED_ROLES — tratamos aqui.
    const sdef = src && src.data && VERBS[src.data.verbKey];
    if (sdef && sdef.isWaMenu) namedHeads.add(e.target);
    // Transferir para a IA: a saída "next" é a RETOMADA (roda quando a IA
    // conclui) — vira um handler nomeado, igual às opções do Menu.
    if (sdef && sdef.isTransferAi && e.sourceHandle === "next") namedHeads.add(e.target);
    if (sdef && sdef.isStartAi && e.sourceHandle === "next") namedHeads.add(e.target);
  });

  // 2) assign deterministic paths
  const pathOf = {};
  pathOf[start.id] = "/";
  let counter = 0;
  Array.from(namedHeads)
    .filter(id => id !== start.id)
    .sort()
    .forEach(id => {
      const custom = nodeById[id] && nodeById[id].data && nodeById[id].data.path;
      counter += 1;
      pathOf[id] = custom && /^\/[\w-]+$/.test(custom) ? custom : `/h${counter}`;
    });

  const resolveHooks = node => {
    const def = VERBS[node.data.verbKey];
    const hooks = {};
    (def.handles || []).forEach(h => {
      if (NAMED_ROLES.indexOf(h.id) < 0) return;
      const target = edgeMap[`${node.id}::${h.id}`];
      if (target && pathOf[target]) {
        hooks[h.id] = pathOf[target];
      } else {
        warnings.push(`"${def.label}" tem ${h.label || h.id} sem destino conectado.`);
      }
    });
    return hooks;
  };

  const compileSequence = (headId, guard) => {
    const arr = [];
    let curId = headId;
    const seen = guard || new Set();
    while (curId && !seen.has(curId)) {
      seen.add(curId);
      const node = nodeById[curId];
      if (!node) break;
      const def = VERBS[node.data.verbKey];
      if (!def || node.data.verbKey === "__start__" || node.data.verbKey === "__trigger__") {
        // start node carries no verb; just follow its next edge
        curId = edgeMap[`${curId}::next`];
        continue;
      }
      if (node.data.disabled) {
        // disabled node: omitted from the flow; continue from its forward edge —
        // "next" for linear nodes, or the single continuation handle (e.g. the
        // gather/dial "action" edge) so the chain re-links across the skip.
        const cur = curId;
        const nextId = edgeMap[`${cur}::next`];
        if (nextId) {
          curId = nextId;
        } else {
          const outs = edges.filter(e => e.source === cur);
          curId = outs.length === 1 ? outs[0].target : undefined;
        }
        continue;
      }
      if (def.isWaMenu) {
        // Menu WhatsApp: envia a mensagem/opções e PAUSA; o motor retoma pela
        // opção escolhida (cursor _wait). Suas opções são handlers nomeados.
        arr.push(compileWaMenu(node));
        break;
      }
      if (def.isTransferAi) {
        // Transferir para a IA: entrega a conversa ao agente; a saída "next" é o
        // handler de retomada (roda quando a IA conclui). Ver engine ai_takeover.
        arr.push(compileTransferAi(node));
        break;
      }
      if (def.isStartAi) {
        // Iniciar conversa com agente de IA: o agente ABRE a conversa (manda a
        // abertura) e assume o número. "next" = retomada. Ver engine ai_start.
        arr.push(compileStartAi(node));
        break;
      }
      if (def.isIvr) {
        // IVR expands inline into [play?, dub?, gather]; its digit outputs are
        // named handlers (routed by our backend at call time, see serveIvr).
        compileIvr(node).forEach(v => arr.push(v));
        break;
      }
      if (def.isBranch) {
        // condicional alcançada por "next" (ex.: código → condicional): embute o
        // objeto de branch como elemento TERMINAL da sequência. O motor/serveNode
        // avalia o campo e roteia para o caso (onde a variável do nó anterior já
        // existe). Antes isto fazia `break` e DESCARTAVA a condicional + tudo
        // depois dela — a chamada caía logo após o nó anterior.
        arr.push(compileBranch(node));
        break;
      }
      const built = def.build(node.data, resolveHooks(node));
      if (built && typeof built === "object" && !Array.isArray(built)) {
        built._name = stepMap[node.id]; // step name for runtime steps.<node>
      }
      arr.push(built);
      if (def.terminal) break;
      curId = edgeMap[`${curId}::next`];
    }
    return arr;
  };

  // IVR (Menu URA): one node -> [play(opening)?, dub(bg)?, gather]. Digit/noinput
  // Menu WhatsApp: vira um verbo `wa_menu` que envia a mensagem/opções (botões
  // interativos) e carrega o cursor de retomada (routes opção→handler + final).
  // O motor (runRealtime) pausa nele e retoma pela opção na próxima mensagem.
  const compileWaMenu = node => {
    const d = node.data || {};
    const options = (d.options || []).filter(Boolean);
    const routes = {};
    options.forEach(opt => {
      const target = edgeMap[`${node.id}::${opt}`];
      if (target && pathOf[target]) routes[opt] = pathOf[target];
      else warnings.push(`Menu WhatsApp: opção "${opt}" sem destino conectado.`);
    });
    const defTarget = edgeMap[`${node.id}::default`];
    if (defTarget && pathOf[defTarget]) routes.default = pathOf[defTarget];

    const connId = d.connectorId || undefined;
    const dest = "{{steps.trigger.event.body.from}}";
    // formato Gupshup (/msg): ≤3 opções → quick_reply; >3 → list
    const message =
      options.length <= 3
        ? { type: "quick_reply", content: { type: "text", text: d.body || "" }, options: options.map(o => ({ type: "text", title: o })) }
        : { type: "list", title: "", body: d.body || "", globalButtons: [{ type: "text", title: "Ver opções" }], items: [{ title: "Opções", options: options.slice(0, 10).map(o => ({ type: "text", title: o, postbackText: o })) }] };
    const txt = s => ({ verb: "connector_action", connectorType: "gupshup", connectorId: connId, label: "WhatsApp: Mensagem", method: "POST", path: "/msg", body: { destination: dest, message: JSON.stringify({ type: "text", text: s }) } });
    return {
      verb: "wa_menu",
      _name: stepMap[node.id],
      send: { verb: "connector_action", connectorType: "gupshup", connectorId: connId, label: "WhatsApp: Menu", method: "POST", path: "/msg", body: { destination: dest, message: JSON.stringify(message) } },
      routes,
      final: (d.final || "").trim() ? txt(d.final) : null,
      ttl: Number(d.ttl) > 0 ? Number(d.ttl) : undefined,
    };
  };

  // Transferir para a IA: vira o verbo `ai_takeover`. A saída "next" (se conectada)
  // é o handler de RETOMADA — o motor roda quando a IA conclui a missão ("seguir o
  // fluxo"), com steps.<este nó>.result disponível. Sem "next", encerra na IA.
  const compileTransferAi = node => {
    const d = node.data || {};
    const nextTarget = edgeMap[`${node.id}::next`];
    const resume = nextTarget && pathOf[nextTarget] ? pathOf[nextTarget] : null;
    return {
      verb: "ai_takeover",
      _name: stepMap[node.id],
      agentId: d.agentId ? Number(d.agentId) : undefined,
      mission: d.mission || "",
      to: d.to || "{{steps.trigger.event.body.from}}",
      exitWords: d.exitWords || undefined,
      connectorId: d.connectorId || undefined,
      sessionMinutes: d.sessionMinutes || undefined,
      resume,
    };
  };

  const compileStartAi = node => {
    const d = node.data || {};
    const nextTarget = edgeMap[`${node.id}::next`];
    const resume = nextTarget && pathOf[nextTarget] ? pathOf[nextTarget] : null;
    return {
      verb: "ai_start",
      _name: stepMap[node.id],
      agentId: d.agentId ? Number(d.agentId) : undefined,
      // ENTRADAS do agente (mesmo mapeamento do Transferir para IA na voz)
      inputs: d.inputs && Object.keys(d.inputs).length ? d.inputs : undefined,
      to: d.to || "",
      message: d.message || "",
      mission: d.mission || undefined,
      exitWords: d.exitWords || undefined,
      connectorId: d.connectorId || undefined,
      sessionMinutes: d.sessionMinutes || undefined,
      resume,
    };
  };

  // outputs become named handlers; the gather carries _ivr + _ivrRoutes so OUR
  // backend routes the pressed digit to the right handler (splitIvr / serveIvr).
  const compileIvr = node => {
    const d = node.data || {};
    const keys = [...(d.digits || []), "noinput"];
    const routes = {};
    keys.forEach(k => {
      const target = edgeMap[`${node.id}::${k}`];
      if (target && pathOf[target]) routes[k] = pathOf[target];
      else if (k !== "noinput")
        warnings.push(`Menu URA: dígito "${k}" sem destino conectado.`);
    });
    const nm = stepMap[node.id];
    const verbs = [];
    if (d.opening) verbs.push({ verb: "play", url: d.opening });
    if (d.bgMusic) {
      // jambonz dub: `play` is the URL string, `loop` is a sibling boolean
      verbs.push({
        verb: "dub",
        action: "addTrack",
        track: "ura-bg",
        play: d.bgMusic,
        loop: true,
        gain: ivrVolumeGain(d.bgVolume),
      });
    }
    // this jambonz instance's say.synthesizer accepts only vendor/voice (it
    // rejects `prompt`), so rate isn't applied on the call — only in the preview.
    const say = { text: d.prompt || "" };
    if (d.voice) say.synthesizer = { voice: d.voice };
    const gather = {
      verb: "gather",
      input: ["digits"],
      numDigits: 1,
      timeout: Number(d.timeout) || 8,
      say,
      _ivr: true,
      _ivrRoutes: routes,
      _ttsVendor: d.ttsVendor || "openai",
      _rate: d.rate || "normal",
      _name: nm,
    };
    if (d.bgMusic) gather._bgTrack = "ura-bg";
    verbs.push(gather);
    return verbs;
  };

  // Fluxo de VOZ? (gatilho de chamada ou algum nó de ligação/áudio). Só na voz a
  // Condição ganha "timeout" e o "default" sem destino vira Desligar — num fluxo de
  // texto (WhatsApp, webhook…) isso era lixo: o default sem destino só encerra o fluxo.
  const VOICE_CATS = ["audio", "call", "queue"];
  const VOICE_KEYS = ["gather", "ivr", "transfer_ai", "record", "listen"];
  const isVoiceFlow =
    (start.data && start.data.triggerType === "inbound_call") ||
    nodes.some((n) => {
      const k = n && n.data && n.data.verbKey;
      const d = k && VERBS[k];
      return !!k && (VOICE_KEYS.includes(k) || (!!d && VOICE_CATS.includes(d.category)));
    });

  const compileBranch = node => {
    const field = node.data.field || "digits";
    const keys = [...(node.data.caseKeys || []), "default", ...(isVoiceFlow ? ["timeout"] : [])];
    const cases = {};
    keys.forEach(k => {
      const target = edgeMap[`${node.id}::${k}`];
      if (target) {
        cases[k] = compileSequence(target);
      } else if (!isVoiceFlow && k === "default") {
        // texto: valor que não casou e sem destino → o fluxo só termina aqui
        cases[k] = [];
      } else if (k === "default" || k === "timeout") {
        // sem destino → ENCERRA a chamada (hangup). ANTES voltava pro "/", o que
        // causava LOOP quando o campo não resolvia (caía sempre no default e
        // re-rodava o fluxo). Loop/repetição INTENCIONAL deve ser DESENHADO —
        // conectando o caso a um destino (ex.: de volta ao gather, com contador).
        cases[k] = [{ verb: "hangup" }];
        warnings.push(
          `Branch "${field}": "${k}" sem destino — a chamada será ENCERRADA. Conecte "${k}" a um destino se quiser outro comportamento (ex.: repetir a pergunta).`
        );
      } else {
        warnings.push(`Branch "${field}": caso "${k}" sem destino conectado.`);
      }
    });
    return { type: "branch", field, cases };
  };

  // 3) compile every named handler
  const handlers = {};
  namedHeads.forEach(id => {
    const node = nodeById[id];
    const path = pathOf[id];
    if (id === start.id) {
      // start: handler "/" is the sequence beginning at its "next" target
      const firstId = edgeMap[`${id}::next`];
      handlers["/"] = firstId ? compileSequence(firstId) : [];
      if (!firstId) warnings.push("O nó de início não está conectado a nenhuma operação.");
      return;
    }
    if (node && VERBS[node.data.verbKey] && VERBS[node.data.verbKey].isBranch) {
      handlers[path] = compileBranch(node);
    } else {
      handlers[path] = compileSequence(id);
    }
  });

  return {
    definition: {
      name: meta.name,
      description: meta.description || "",
      trigger: meta.trigger || "inbound",
      handlers,
    },
    warnings,
  };
}
